import React, { useContext, useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import ApplicationContext from '../../../../ApplicationContext';
import IfmsModal from '../../../../ui/IfmsModal';
import SecondaryButton from '../../../../ui/SecondaryButton';
import EditableTable, { CellData, TableCellAligns, TableCellTypes } from '../../../../ui/tables/editing/EditableTable';
import { ApplicableTableSettings, airExportEawbHistoryList } from '../../../../tableSettings';
import { EawbHistory, EawbEdiStatusText, EdiStatusDetailText } from '../../../../fms.core/pims/Eawb';
import { formatDateTime } from '../../../../dateUtils';

// 이력 Status: 2자리 StatusDetail(CCI 시절 코드) → 3자리 표기 우선,
// 다음 1자리 전송상태(A/S/E..) → 문구, 빈값은 공백 (3.3 FrmEawbHistory 룩업과 동일).
const historyStatusText = (status: string) =>
  EdiStatusDetailText[status] ?? (status ? (EawbEdiStatusText[status] ?? status) : '');

// e-AWB 이력 팝업 (ifms3.3 FrmEawbHistory 대응 — IfmsModal + 공용 EditableTable 그리드).
// AeEawbView 의 상태 링크와 MAWB/HAWB 목록의 FWB/FHL/FRT Status 셀 클릭이 공용으로 사용한다
// (3.3 ListRenderer.ShowEawbHistoryView 가 목록 셀 클릭에서 같은 팝업을 열던 것과 동일).
function EawbHistoryPopup({ mawbNo, onClose }: { mawbNo: string, onClose: VoidFunction }) {
  const appContext = useContext(ApplicationContext);
  const [rows, setRows] = useState<EawbHistory[] | null>(null);

  useEffect(() => {
    (async () => {
      const result = await appContext.api.getEawbHistory(mawbNo);
      setRows((result && result.success) ? (result.entities ?? []) : []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mawbNo]);

  const gridData = (rows ?? []).map((h, i) => ({
    indicator: String(i + 1),
    eventDate: formatDateTime(h.eventDate),
    type: h.type,
    hawbNo: h.hawbNo,
    status: historyStatusText(h.status),
    statusMessage: h.statusMessage,
  }));

  const tableOptions: ApplicableTableSettings = {
    customTableId: 'airExport-eawb-history-list',
    tableName: 'airExportEawbHistoryList',
    selectable: false,
    ...airExportEawbHistoryList,
    data: gridData,
    rowRenderers: airExportEawbHistoryList.columns.map(c => ({ accessor: c.accessor })),
    getCellData: (accessor: string, _rowData: any, rowIndex: number): CellData | undefined => {
      const row = gridData[rowIndex];
      if (!row) return undefined;
      // Type/Status 중앙정렬 (3.3 FrmEawbHistory HAlign Center 동일)
      if (accessor === 'type' || accessor === 'status')
        return { type: TableCellTypes.Text, value: (row as any)[accessor] ?? '', align: TableCellAligns.Center };
      return { type: TableCellTypes.Text, value: (row as any)[accessor] ?? '' };
    },
    isCellLock: () => true,
  };

  return (
    <IfmsModal isBlueHeader cancelOnEsc onCancel={onClose} draggable isOpen title={`e-AWB History — ${mawbNo}`} width="860px">
      {rows == null ? (
        <div style={{ position: 'relative', top: '-25px' }}>
          <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
            <Skeleton count={1} height={2} />
          </SkeletonTheme>
        </div>
      ) : (
        <div className='report-popup'>
          {/* 높이는 tableSettings airExportEawbHistoryList.height(10행 고정)가 결정 — 남는 영역은 그리드가 빈 공간으로 채움 */}
          <div>
            <EditableTable
              tableOptions={tableOptions}
              deleteLine={() => false}
              addLine={() => undefined}
              canAdd={false}
              hideAddBtn
              canDeleteRow={() => false}
            />
          </div>
          <div className='report-footer'>
            <SecondaryButton size="regular" style={{ width: '90px' }} onClick={onClose}>
              Close
            </SecondaryButton>
          </div>
        </div>
      )}
    </IfmsModal>
  );
}

export default React.memo(EawbHistoryPopup);
